/**
 * intelligencePersonalization.ts — typed wire contracts for the Phibian
 * Intelligence personalization domain (profiles + memory).
 *
 * Every schema here re-declares a shape the API domain already serves (see
 * `apps/api/src/domains/intelligence/personalization/{profiles,memory}.ts`).
 * The API package is deliberately NOT imported: the admin UI must validate
 * and render these shapes without pulling server code, and the two
 * declarations must drift loudly rather than silently when the domain
 * evolves. The source of truth for each schema is named in its doc comment.
 *
 * Deliberately ABSENT (withdrawn, lead-owned): recommendation persistence
 * and approval semantics. Nothing here creates, validates or persists
 * recommendations; `recommendationId` appears only as opaque uuid attribution
 * inside user-profile `outcomeRefs` — mirroring profiles.ts exactly. There
 * are no HTTP route paths in this module: the lead owns the routes; these
 * are contract types only.
 *
 * The authority fence holds client-side too: every profile schema is
 * `.strict()` (a grant/permission/scope-shaped key can never parse), the
 * memory payload is `.strict()` on the refs/metadata allowlist, and the
 * grant-shaped/authority-shaped key patterns from profiles.ts are re-declared
 * verbatim so a client can scan raw payloads the same way validateProfile
 * does.
 *
 * Sources of truth in the API domain:
 *   - ProfileKind / ProfileSource / ProfileSourceType / ProfileMetadata /
 *     organization|role|user profiles / versioning envelopes / fence
 *     patterns → `domains/intelligence/personalization/profiles.ts`
 *   - EvidenceClass (canonical 5) / retention classes / revocation states /
 *     sensitivity classification / MemoryItem envelope →
 *     `domains/intelligence/personalization/memory.ts`
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Shared primitives
// ---------------------------------------------------------------------------
/** Lowercase hex sha256: exactly 64 hex chars (profiles.ts contentHashSchema). */
const SHA256_HEX = /^[0-9a-f]{64}$/i;
/** ISO 8601 timestamp with explicit offset (e.g. 2026-08-23T22:00:00.000Z). */
const isoTimestampSchema = z.string().datetime({ offset: true });
// ---------------------------------------------------------------------------
// Memory-owned vocabulary (personalization/memory.ts) — declared FIRST
// because profiles.ts imports EVIDENCE_CLASSES from memory.ts; the client
// mirrors that dependency direction.
// ---------------------------------------------------------------------------
/** The canonical five evidence classes (memory.ts EVIDENCE_CLASSES). */
export const evidenceClassSchema = z.enum([
    'AUTHORITATIVE',
    'EXPLICIT_PREFERENCE',
    'OBSERVED_PATTERN',
    'INFERRED',
    'OUTCOME',
]);
export const EVIDENCE_CLASSES = evidenceClassSchema.options;
/** Org-wide sensitivity vocabulary (memory.ts SENSITIVITY_CLASSIFICATIONS). */
export const sensitivityClassificationSchema = z.enum(['internal', 'sensitive', 'restricted']);
// ===========================================================================
// PROFILE CONTRACTS (personalization/profiles.ts)
// ===========================================================================
// ---------------------------------------------------------------------------
// Shared metadata block (architecture §2) — every profile of every kind.
// ---------------------------------------------------------------------------
export const profileSourceSchema = z.enum([
    'system',
    'orgAdmin',
    'user',
    'doctrine',
    'observed',
    'inferred',
]);
/** The profile kind — identity discriminator (organization | role | user). */
export const profileKindSchema = z.enum(['organization', 'role', 'user']);
/**
 * Provenance class of this version's origin. NOT the profile kind: the kind
 * discriminator is `profileKind`; `sourceType` carries provenance semantics
 * only (who/what produced this version and how).
 */
export const profileSourceTypeSchema = z.enum([
    'authored',
    'seeded',
    'derived',
    'inferred',
    'policy',
    'preference',
]);
/** Revocation/standing state, shared by profiles and memory items. */
export const revocationStateSchema = z.enum(['active', 'revoked', 'superseded']);
/** ISO 3166-2 style jurisdiction code where relevant (e.g. 'US-TX'). */
export const jurisdictionSchema = z
    .string()
    .trim()
    .regex(/^[A-Z]{2}(-[A-Z0-9]{1,3})?$/, 'ISO 3166-2 style jurisdiction code (e.g. US-TX)')
    .optional();
const metadataShape = {
    /** Stable id of THIS version record (a new version gets a new id). */
    profileId: z.string().uuid(),
    /** Tenant the profile belongs to. */
    orgId: z.string().uuid(),
    /** Kind discriminator — organization | role | user (identity, not provenance). */
    profileKind: profileKindSchema,
    /** 1-based, strictly increasing across a profile's version chain. */
    profileVersion: z.number().int().positive(),
    /** When this version takes effect (ISO 8601). */
    effectiveFrom: isoTimestampSchema,
    /** When this version stops being effective (ISO 8601); null while current. */
    effectiveTo: isoTimestampSchema.nullable(),
    source: profileSourceSchema,
    sourceType: profileSourceTypeSchema,
    /** Provenance — REQUIRED. Principal id that authored this version, or 'system'. */
    author: z.string().trim().min(1).max(200),
    /** 0..1. Authoritative/admin-authored versions are 1; inferred < 1 always. */
    confidence: z.number().min(0).max(1),
    jurisdiction: jurisdictionSchema,
    revocationState: revocationStateSchema,
    /** sha256 over the canonical form of every field except contentHash. */
    contentHash: z.string().regex(SHA256_HEX),
};
/** The shared §2 metadata block as a standalone strict schema. */
export const profileMetadataSchema = z.object(metadataShape).strict();
// ---------------------------------------------------------------------------
// Reference pointers — pointers only, never denormalized copies.
// ---------------------------------------------------------------------------
/**
 * A typed pointer into a live record owned by another domain. The ONLY
 * payload allowed is `refId` (+ optional display label); a ref carrying
 * denormalized data is rejected by the strict schemas.
 */
export const profileRefSchema = z
    .object({
    refId: z.string().trim().min(1).max(200),
    label: z.string().trim().max(200).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Organization Intelligence Profile (architecture §2A)
// ---------------------------------------------------------------------------
/** 24h clock time, 'HH:MM'. */
const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM (24h)');
const dayOfWeekSchema = z.enum(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']);
const organizationProfileContentShape = {
    name: z.string().trim().min(1).max(200),
    /** Internal vocabulary: org term → meaning (e.g. 'IS' → 'individual served'). */
    terminology: z
        .record(z.string().trim().min(1).max(64), z.string().trim().min(1).max(500))
        .default({}),
    operatingHours: z
        .object({
        /** IANA timezone the org schedules in (e.g. 'America/Chicago'). */
        timezone: z.string().trim().min(1).max(64),
        weekly: z
            .array(z
            .object({
            day: dayOfWeekSchema,
            closed: z.boolean().default(false),
            opensAt: timeOfDaySchema.optional(),
            closesAt: timeOfDaySchema.optional(),
        })
            .strict())
            .max(7),
    })
        .strict(),
    /** Overtime thresholds beyond which an alert/escalation is expected. */
    overtimeThresholds: z
        .object({
        dailyHours: z.number().positive().optional(),
        weeklyHours: z.number().positive().optional(),
        consecutiveDays: z.number().int().positive().optional(),
    })
        .strict()
        .default({}),
    schedulingRules: z.array(z.string().trim().min(1).max(1000)).default([]),
    escalationPolicies: z
        .array(z
        .object({
        trigger: z.string().trim().min(1).max(500),
        route: z.string().trim().min(1).max(200),
        urgency: z.enum(['immediate', 'same_day', 'next_day', 'as_needed']),
    })
        .strict())
        .default([]),
    documentationExpectations: z.array(z.string().trim().min(1).max(1000)).default([]),
    supervisionRequirements: z.array(z.string().trim().min(1).max(1000)).default([]),
    complianceRules: z.array(z.string().trim().min(1).max(1000)).default([]),
    /** Preferred KPIs — keys from the dashboard metric registry. */
    preferredKpis: z.array(z.string().trim().min(1).max(96)).default([]),
    /** Preferred dashboards — pointers (spec ids), never inline specs. */
    preferredDashboardSpecs: z.array(z.string().uuid()).default([]),
    /** Org risk thresholds by metric key. */
    riskThresholds: z.record(z.string().trim().min(1).max(96), z.number().finite()).default({}),
    workflowRules: z.array(z.string().trim().min(1).max(1000)).default([]),
    /** Free-form operating preferences (typed primitives only). */
    operatingPreferences: z
        .record(z.string().trim().min(1).max(96), z.union([z.string().max(1000), z.number(), z.boolean()]))
        .default({}),
    // Reference fields: pointers only, resolved live at retrieval time.
    siteRefs: z.array(profileRefSchema).default([]),
    programRefs: z.array(profileRefSchema).default([]),
    payerRefs: z.array(profileRefSchema).default([]),
    staffStructureRefs: z.array(profileRefSchema).default([]),
    connectedSystemRefs: z.array(profileRefSchema).default([]),
    doctrineRefs: z.array(profileRefSchema).default([]),
    /** SOP/policy refs into the knowledge domain. */
    policyRefs: z.array(profileRefSchema).default([]),
};
export const organizationProfileContentSchema = z.object(organizationProfileContentShape).strict();
export const organizationProfileSchema = z
    .object({
    ...metadataShape,
    profileKind: z.literal('organization'),
    ...organizationProfileContentShape,
})
    .strict();
// ---------------------------------------------------------------------------
// Role Intelligence Profile (architecture §2B) — behavioral defaults, ZERO
// grants. No permission, grant, scope or authority field of any shape.
// ---------------------------------------------------------------------------
/** Tenancy discriminator: platform seeds vs org-authored role rows. */
export const roleTenancySchema = z.enum(['platform_seed', 'org']);
/** The seven supported role keys. */
export const roleKeySchema = z.enum([
    'ceo',
    'supervisor',
    'office_manager',
    'compliance',
    'peer_specialist',
    'scheduler',
    'field_worker',
]);
export const ROLE_KEYS = roleKeySchema.options;
/** Metric families from the dashboard metric catalog (read-only). */
export const metricFamilyKeySchema = z.enum([
    'workforce',
    'scheduling',
    'field_operations',
    'documentation',
    'exceptions',
    'service_client',
]);
/** Tool families a role should consider first (retrieval order hint). */
const retrievalFocusSchema = z.enum([
    'workforce',
    'supervision',
    'connectors',
    'dashboards',
    'knowledge',
    'scheduling',
    'compliance',
    'documentation',
    'alerts',
]);
export const timeHorizonSchema = z.enum(['day', 'week', 'month', 'quarter', 'year']);
const roleProfileContentShape = {
    roleKey: roleKeySchema,
    /** Tenancy discriminator: 'platform_seed' only for system-authored seeds. */
    roleTenancy: roleTenancySchema,
    /** Tool families to consider first, in preference order. */
    retrievalFocus: z.array(retrievalFocusSchema).default([]),
    /** Prioritization weights per metric family (0..10). */
    prioritizationWeights: z.record(metricFamilyKeySchema, z.number().min(0).max(10)).default({}),
    responseStructure: z.enum(['brief', 'detailed', 'narrative', 'dashboard']),
    explanationDepth: z.enum(['minimal', 'standard', 'detailed']),
    /**
     * Dashboard emphasis defaults: view-layer hints over an EXISTING validated
     * spec. Never changes which metrics exist or what the viewer may see.
     */
    preferredDashboardEmphasis: z
        .object({
        grouping: z.enum(['family', 'status', 'none']).default('family'),
        prominence: z.enum(['kpi_first', 'alerts_first', 'flat']).default('kpi_first'),
    })
        .strict()
        .default({ grouping: 'family', prominence: 'kpi_first' }),
    preferredTimeHorizon: timeHorizonSchema,
    /** Preferred KPI order — keys from the dashboard metric registry. */
    kpiOrdering: z.array(z.string().trim().min(1).max(96)).default([]),
    notificationDefaults: z
        .object({
        digestFrequency: z.enum(['realtime', 'hourly', 'daily', 'weekly']).default('daily'),
        quietHours: z.object({ start: timeOfDaySchema, end: timeOfDaySchema }).strict().optional(),
    })
        .strict()
        .default({ digestFrequency: 'daily' }),
};
export const roleProfileContentSchema = z.object(roleProfileContentShape).strict();
export const roleProfileSchema = z
    .object({
    ...metadataShape,
    profileKind: z.literal('role'),
    ...roleProfileContentShape,
})
    .strict();
// ---------------------------------------------------------------------------
// User Intelligence Profile (architecture §2C) — job-relevant only. Identity
// and work context are POINTERS; preferences are explicit and user-editable;
// observed patterns carry confidence + expiry and are never authoritative.
// No protected-trait fields — the strict schema makes that structural.
// ---------------------------------------------------------------------------
const observedPatternShape = {
    patternType: z.enum([
        'frequent_dashboard',
        'recurring_query',
        'recommendation_reviewed',
        'recurring_issue',
        'common_task',
    ]),
    /** Pointer to the observed subject (dashboard id, query key, …). */
    reference: z.string().trim().min(1).max(500),
    /** Observed patterns are presentation-only, never authoritative. */
    evidenceClass: evidenceClassSchema,
    confidence: z.number().min(0).max(1),
    observedAt: isoTimestampSchema,
    /** Observed patterns MUST expire; expiry is required, not optional. */
    expiresAt: isoTimestampSchema,
    occurrences: z.number().int().positive(),
};
const userProfileContentShape = {
    roleKey: roleKeySchema,
    /** Work-context pointers — resolved live, never denormalized copies. */
    siteRef: z.string().uuid().optional(),
    programRefs: z.array(z.string().uuid()).default([]),
    supervisorRef: z.string().uuid().optional(),
    directReportRefs: z.array(z.string().uuid()).default([]),
    assignedClientRefs: z.array(z.string().uuid()).default([]),
    workflowRefs: z.array(profileRefSchema).default([]),
    responsibilityRefs: z.array(z.string().trim().min(1).max(200)).default([]),
    /**
     * Explicit, user-editable preferences (class EXPLICIT_PREFERENCE). These
     * affect presentation and prioritization only — they can never override
     * policy, authorization or compliance content.
     */
    explicitPreferences: z
        .object({
        verbosity: z.enum(['concise', 'detailed']).default('concise'),
        presentation: z.enum(['narrative', 'dashboard', 'mixed']).default('mixed'),
        kpiOrdering: z.array(z.string().trim().min(1).max(96)).default([]),
        timeHorizon: timeHorizonSchema.default('week'),
        notificationPreferences: z
            .object({
            digestFrequency: z.enum(['realtime', 'hourly', 'daily', 'weekly']).default('daily'),
            quietHours: z
                .object({ start: timeOfDaySchema, end: timeOfDaySchema })
                .strict()
                .optional(),
        })
            .strict()
            .optional(),
        preferredViews: z.array(z.string().trim().min(1).max(96)).default([]),
    })
        .strict(),
    /**
     * Behavioral work history (class OBSERVED_PATTERN): weak signals with
     * confidence + mandatory expiry. Never authoritative, never a fact about
     * the person — only about their product behavior.
     */
    observedPatterns: z.array(z.object(observedPatternShape).strict()).default([]),
    /**
     * Learning signals (class OUTCOME): pointers into the lead-owned
     * recommendation/feedback records. `recommendationId` is OPAQUE uuid
     * attribution only — it creates, grants and persists nothing.
     */
    outcomeRefs: z
        .array(z
        .object({
        recommendationId: z.string().uuid(),
        decision: z.enum(['accepted', 'rejected', 'modified', 'deferred']),
    })
        .strict())
        .default([]),
};
export const userProfileContentSchema = z.object(userProfileContentShape).strict();
export const userProfileSchema = z
    .object({
    ...metadataShape,
    profileKind: z.literal('user'),
    ...userProfileContentShape,
})
    .strict();
/** The discriminated profile union — discriminated on `profileKind`. */
export const anyProfileSchema = z.discriminatedUnion('profileKind', [
    organizationProfileSchema,
    roleProfileSchema,
    userProfileSchema,
]);
// ---------------------------------------------------------------------------
// Versioning envelope (profiles.ts VersionTransition<T>, T = AnyProfile)
// ---------------------------------------------------------------------------
/**
 * The append/versioned transition envelope: a policy change is a NEW version
 * with a new profileId and effectiveFrom; the previous record becomes
 * `superseded` with `effectiveTo` set to the new version's effectiveFrom.
 * Mirrors profiles.ts `VersionTransition<T>` structurally.
 */
export const profileVersionTransitionSchema = z.object({
    /** The previous record, now `superseded` (effectiveTo = next.effectiveFrom). */
    previous: anyProfileSchema,
    /** The new current version (version+1, active, fresh profileId). */
    next: anyProfileSchema,
});
// ---------------------------------------------------------------------------
// Authority fence patterns — re-declared VERBATIM from profiles.ts so a
// client can scan raw payloads exactly like validateProfile does. The strict
// schemas reject these keys outright; these patterns make the prohibition
// visible for raw payloads that never parse.
// ---------------------------------------------------------------------------
/** Protected-trait key pattern (architecture §23). */
export const PROTECTED_TRAIT_KEY_PATTERN = /(religio|politic|sexual|gender|orientat|health|mental|disabilit|ethnici|race|marital|pregnan|personality|psycholog|diagnos|addiction|substance|belief|ideolog)/i;
/** Grant-shaped key pattern (invariant I-2): profiles are data, never authority. */
export const GRANT_SHAPED_KEY_PATTERN = /(grant|permission|scope|authoriz|privilege|entitlement|access[_ ]?level|can[_ ]?(read|write|access))/i;
/** Role→permission mapping key pattern (§17.3 fence extension). */
export const ROLE_PERMISSION_MAPPING_KEY_PATTERN = /(role[_ -]?to[_ -]?perm|role[_ -]?perms?|perm[_ -]?(map|mapping|set|list)|perms?[_ -]?(map|mapping|set|list))/i;
/** Scope-allowlist key pattern (§17.3 fence extension). */
export const SCOPE_ALLOWLIST_KEY_PATTERN = /(allowlist|allow[_ -]?list|whitelist|white[_ -]?list|allowed[_ -]?scopes?|permitted[_ -]?scopes?|scope[_ -]?(allow|white|permitted)[_ -]?lists?)/i;
export function isProtectedTraitKey(key) {
    return PROTECTED_TRAIT_KEY_PATTERN.test(key);
}
export function isGrantShapedKey(key) {
    return GRANT_SHAPED_KEY_PATTERN.test(key);
}
/** True when a key names a role→permission mapping or a scope allowlist. */
export function isAuthorityShapedKey(key) {
    return (isGrantShapedKey(key) ||
        ROLE_PERMISSION_MAPPING_KEY_PATTERN.test(key) ||
        SCOPE_ALLOWLIST_KEY_PATTERN.test(key));
}
function isPlainObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}
/** Recursively collect paths of keys matching `predicate`. */
function scanKeys(value, predicate, path = '') {
    const hits = [];
    if (Array.isArray(value)) {
        value.forEach((entry, index) => {
            hits.push(...scanKeys(entry, predicate, `${path}[${index}]`));
        });
        return hits;
    }
    if (isPlainObject(value)) {
        for (const [key, entry] of Object.entries(value)) {
            const keyPath = path === '' ? key : `${path}.${key}`;
            if (predicate(key))
                hits.push(keyPath);
            hits.push(...scanKeys(entry, predicate, keyPath));
        }
    }
    return hits;
}
export function scanProtectedTraitKeys(value) {
    return scanKeys(value, isProtectedTraitKey);
}
export function scanGrantShapedKeys(value) {
    return scanKeys(value, isGrantShapedKey);
}
/** Scan a raw payload for any authority-shaped key (§17.3 fence). */
export function scanAuthorityShapedKeys(value) {
    return scanKeys(value, isAuthorityShapedKey);
}
// ===========================================================================
// MEMORY CONTRACTS (personalization/memory.ts)
// ===========================================================================
// ---------------------------------------------------------------------------
// Typed domain vocabulary
// ---------------------------------------------------------------------------
export const memorySubjectTypeSchema = z.enum(['org', 'role', 'user', 'operational']);
export const memoryScopeSchema = z.enum(['org', 'site', 'program', 'role', 'user']);
export const retentionClassSchema = z.enum([
    'SESSION_MEMORY',
    'WORKING_MEMORY',
    'DURABLE_WORK_MEMORY',
    'ORGANIZATION_MEMORY',
    'OUTCOME_MEMORY',
]);
/**
 * Standing order, strongest first (memory.ts EVIDENCE_CLASS_AUTHORITY_ORDER):
 * authoritative > outcome > explicit preference > observed > inferred.
 */
export const EVIDENCE_CLASS_AUTHORITY_ORDER = [
    'AUTHORITATIVE',
    'OUTCOME',
    'EXPLICIT_PREFERENCE',
    'OBSERVED_PATTERN',
    'INFERRED',
];
export const EVIDENCE_CLASS_AUTHORITY_RANK = {
    AUTHORITATIVE: 4,
    OUTCOME: 3,
    EXPLICIT_PREFERENCE: 2,
    OBSERVED_PATTERN: 1,
    INFERRED: 0,
};
/** Numeric standing of an evidence class per §21 (weakest = 0). */
export function authorityRank(evidenceClass) {
    return EVIDENCE_CLASS_AUTHORITY_RANK[evidenceClass];
}
// ---------------------------------------------------------------------------
// Memory payload — references and metadata ONLY (structural allowlist)
// ---------------------------------------------------------------------------
/** Permission key shape (memory.ts PERMISSION_KEY_SHAPE): dotted groups, colon action. */
const PERMISSION_KEY_SHAPE = /^[a-z0-9_-]+(\.[a-z0-9_-]+)*:[a-z0-9_*:-]+$/i;
/** sha256 hex, exactly 64 chars — the evidence-manifest shape. */
const HASH64_HEX = /^[0-9a-f]{64}$/i;
/**
 * One pointer into an evidence-manifest entry (content-hash-bearing). Field
 * types mirror the MemoryPayloadRef interface; `refs` entries may also be
 * plain non-empty manifest-ref strings (memory.ts validateRefEntry accepts
 * both).
 */
export const memoryPayloadRefSchema = z
    .object({
    manifestRef: z.string().trim().min(1).max(128),
    refType: z.string().max(200).optional(),
    label: z.string().max(200).optional(),
    /** Hash of the referenced evidence content, when known (64-hex sha256). */
    contentHash: z.string().regex(HASH64_HEX).optional(),
})
    .strict();
/**
 * The refs/metadata-only payload a memory item carries. The allowlist is
 * structural, not prose (§13): a payload cannot contain a client identifier
 * because there is no field for one, and `.strict()` rejects any other key.
 * Bounds mirror the memory.ts validator constants (KIND_MAX 64, LABELS_MAX
 * 16 × LABEL_MAX 64, NOTE_MAX 280, REF_ID_MAX 128, COUNTS_KEY_MAX 48).
 */
export const memoryPayloadSchema = z
    .object({
    /** Content kind tag — short metadata, not free prose. */
    kind: z.string().trim().min(1).max(64),
    /** Pointers into the evidence manifest / system of record. */
    refs: z.array(z.union([z.string().trim().min(1).max(128), memoryPayloadRefSchema])).optional(),
    /** Short metadata labels (bounded, non-empty). */
    labels: z.array(z.string().trim().min(1).max(64)).max(16).optional(),
    /** Bounded operational summary — metadata about the work, never client data. */
    note: z.string().max(280).optional(),
    /** Metadata counts keyed by label. Values are numbers, never records. */
    counts: z.record(z.string().max(48), z.number().int().min(0)).optional(),
})
    .strict();
// ---------------------------------------------------------------------------
// Memory item envelope
// ---------------------------------------------------------------------------
/** Secret-shaped envelope keys (memory.ts ENVELOPE_SENSITIVE_KEY). */
const ENVELOPE_SENSITIVE_KEY = /(secret|token|password|credential|api[-_.]?key|authorization|private[-_.]?key)/i;
/**
 * The metadata envelope every memory item carries (§2, §6). Mirrors
 * memory.ts MemoryItem field-for-field: append-only (a change is a new
 * memoryId), refs/metadata-only payload, requiredPermissions as a READ gate
 * (they grant nothing).
 *
 * Unknown envelope keys are TOLERATED for forward-compatible evolution —
 * exactly like validateMemoryItem — unless the key is secret-shaped, which
 * fails the parse (the `sensitive_field_detected` mirror).
 */
export const memoryItemSchema = z
    .object({
    memoryId: z.string().min(1).max(96),
    orgId: z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i),
    subjectType: memorySubjectTypeSchema,
    subjectId: z.string().min(1),
    scope: memoryScopeSchema,
    /** Named origin (e.g. 'org policy v3', 'worker-42 dashboard usage'). */
    source: z.string().min(1),
    /** Origin kind (system | orgAdmin | user | doctrine | observed | inferred | outcome). */
    sourceType: z.string().min(1),
    /** Creation time, ms epoch. */
    timestamp: z.number().finite().min(0),
    /** First ms at which the item is visible to retrieval. */
    effectiveFrom: z.number().finite().min(0),
    /** Last ms at which the item is visible; undefined = no scheduled expiry. */
    effectiveTo: z.number().finite().min(0).optional(),
    /** 0..1. */
    confidence: z.number().min(0).max(1),
    evidenceClass: evidenceClassSchema,
    /** True when explicitly provided (preference, policy); false when derived. */
    explicitFlag: z.boolean(),
    /** Evidence-manifest refs backing this item. */
    provenanceRefs: z.array(z.string().min(1)),
    retentionClass: retentionClassSchema,
    sensitivityClassification: sensitivityClassificationSchema,
    /** Permission keys a caller must hold (via can()) to READ this item — a gate, never a grant. */
    requiredPermissions: z.array(z.string().regex(PERMISSION_KEY_SHAPE)).min(1),
    revocationState: revocationStateSchema,
    /** sha256 (lowercase hex) of the canonical form of `payload`. */
    contentHash: z.string().regex(HASH64_HEX),
    payload: memoryPayloadSchema,
})
    .passthrough()
    .superRefine((item, ctx) => {
    if (item.effectiveTo !== undefined && item.effectiveTo < item.effectiveFrom) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'effectiveTo must not precede effectiveFrom',
            path: ['effectiveTo'],
        });
    }
    for (const key of Object.keys(item)) {
        if (!KNOWN_ENVELOPE_KEYS.has(key) && ENVELOPE_SENSITIVE_KEY.test(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `field '${key}' looks like a secret and does not belong in a memory envelope`,
                path: [key],
            });
        }
    }
});
/** The declared envelope keys (memory.ts KNOWN_ENVELOPE_KEYS). */
const KNOWN_ENVELOPE_KEYS = new Set([
    'memoryId',
    'orgId',
    'subjectType',
    'subjectId',
    'scope',
    'source',
    'sourceType',
    'timestamp',
    'effectiveFrom',
    'effectiveTo',
    'confidence',
    'evidenceClass',
    'explicitFlag',
    'provenanceRefs',
    'retentionClass',
    'sensitivityClassification',
    'requiredPermissions',
    'revocationState',
    'contentHash',
    'payload',
]);
// ===========================================================================
// SUBJECT ACCESS (domains/intelligence/personalization/subjectAccessRoutes.ts)
// ===========================================================================
//
// The wire shapes of the two doors a data subject has onto their own memory:
//
//   GET  /api/intelligence/memory/about-me
//   POST /api/intelligence/memory/about-me/revocations
//
// NEITHER TAKES A SUBJECT PARAMETER, and that is a contract, not an omission:
// the subject is the authenticated caller. A client that wants to read another
// person's memory is asking for a DIFFERENT capability, with its own permission
// key and its own audit trail; it is not this request with a field added, and a
// client adding one gets a 400 rather than somebody else's data.
//
// `memoryRevocationRequestSchema` is `.strict()` for exactly that reason.
/** One record as the subject sees it. Refs and metadata; never resolved evidence. */
export const subjectMemoryRecordSchema = z.object({
    memoryId: z.string().regex(/^mem_[0-9a-f]{64}$/),
    subjectType: z.enum(['org', 'role', 'user', 'operational']),
    scope: z.enum(['org', 'site', 'program', 'role', 'user']),
    /** Where it came from. `explicitFlag` is "you told us" vs "we inferred it". */
    source: z.string(),
    sourceType: z.string(),
    explicitFlag: z.boolean(),
    provenanceRefs: z.array(z.string()),
    /** How much authority it has. Rank 0 is strongest (AUTHORITATIVE). */
    evidenceClass: evidenceClassSchema,
    evidenceAuthorityRank: z.number().int().min(0),
    confidence: z.number().min(0).max(1),
    /** How long it lasts. */
    retentionClass: z.enum([
        'SESSION_MEMORY',
        'WORKING_MEMORY',
        'DURABLE_WORK_MEMORY',
        'ORGANIZATION_MEMORY',
        'OUTCOME_MEMORY',
    ]),
    retentionPolicy: z.string(),
    sensitivityClassification: sensitivityClassificationSchema,
    timestamp: isoTimestampSchema,
    effectiveFrom: isoTimestampSchema,
    effectiveTo: isoTimestampSchema.nullable(),
    purgeDueAt: isoTimestampSchema.nullable(),
    /**
     * RETAINED AND IN-USE ARE DIFFERENT FACTS. A revoked or expired record is
     * still listed — it is still held — and `intelligenceUsesThis` is the field
     * that says whether it still reaches the model. A surface that rendered only
     * one of these would tell a person something untrue either way.
     */
    revocationState: revocationStateSchema,
    revokedAt: isoTimestampSchema.nullable(),
    revocationReason: z.string().nullable(),
    intelligenceUsesThis: z.boolean(),
    /** Which keys gate its USE. Not the gate on this read; see the module header. */
    requiredPermissions: z.array(z.string()),
    contentHash: z.string().regex(SHA256_HEX),
    payload: memoryPayloadSchema,
});
/**
 * WHY CORRECTION IS ABSENT, carried in the response so the screen can say it
 * rather than leaving a person to guess. A memory item is identified by the
 * hash of its own contents, so an edited item is a different item; and editing
 * an OBSERVED_PATTERN would produce a record claiming an observation nobody
 * made, still carrying the authority of having been observed.
 */
export const memoryCorrectionContractSchema = z.object({
    supported: z.literal(false),
    reason: z.literal('memory_is_revocable_not_correctable'),
    detail: z.string(),
    remedy: z.literal('revoke'),
});
export const subjectMemoryResponseSchema = z.object({
    /** Echoed, so a response can never be mistaken for somebody else's record. */
    subject: z.object({ userId: z.string().uuid() }),
    records: z.array(subjectMemoryRecordSchema),
    /**
     * THREE ANSWERS, NOT ONE NUMBER. "Nothing is recorded about you" and
     * "everything recorded about you has been revoked" are different sentences,
     * and a screen that could not tell them apart would say the wrong one.
     */
    held: z.number().int().min(0),
    active: z.number().int().min(0),
    revoked: z.number().int().min(0),
    windowExhausted: z.boolean(),
    correction: memoryCorrectionContractSchema,
});
/**
 * The revocation request. ONE field, `.strict()`, and no reason: the reason is
 * fixed server-side to the closed-vocabulary token `user_request`, so no prose
 * a person types can reach an audit row.
 */
export const memoryRevocationRequestSchema = z
    .object({ memoryId: z.string().regex(/^mem_[0-9a-f]{64}$/) })
    .strict();
export const memoryRevocationResponseSchema = z.object({
    memoryId: z.string().regex(/^mem_[0-9a-f]{64}$/),
    revocationState: z.enum(['revoked', 'superseded']),
    effectiveTo: isoTimestampSchema,
    /** Said explicitly, because this is the promise being made. */
    intelligenceUsesThis: z.literal(false),
});
//# sourceMappingURL=intelligencePersonalization.js.map